import { useState } from "react";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X, CheckCircle2, Share2, Lock } from "lucide-react";
import { createPaymentOrder, createTestPaymentOrder, verifyPayment, inr, formatApiError } from "@/lib/api";
import { getReferral } from "@/lib/referral";

const sel = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

export function planText(advancePct) {
    const mid = Math.round((100 - advancePct) * (2 / 3));
    return `${advancePct}% now to block seats · ${mid}% on booking confirmation · ${100 - advancePct - mid}% before departure`;
}

export function PayAdvance({ pkg, calc, tier, estimate }) {
    const { Razorpay } = useRazorpay();
    const [open, setOpen] = useState(false);
    const [customer, setCustomer] = useState({ name: "", phone: "", email: "" });
    const [coupon, setCoupon] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    if (!estimate?.advance) return null;

    const startPayment = async () => {
        if (!customer.name.trim() || !/^\d{10,12}$/.test(customer.phone)) {
            toast.error("Please add your name and a valid 10-digit mobile number.");
            return;
        }
        setBusy(true);
        try {
            const order = await createPaymentOrder({
                package_slug: pkg.slug,
                customer,
                coupon_code: coupon.trim() || undefined,
                associate_code: getReferral() || undefined,
                estimate: {
                    tier, adults: calc.adults, children: calc.children,
                    child_ages: calc.childAges.slice(0, calc.children),
                    rooms: calc.rooms, transport_mode: calc.transport_mode,
                    departure_city: calc.departure_city, addons: calc.addons,
                    travel_date: calc.travel_date || null,
                },
            });
            const rzp = new Razorpay({
                key: order.key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "GhumneChaley.com",
                description: `${pkg.name} — booking advance`,
                order_id: order.order_id,
                prefill: { name: customer.name, contact: customer.phone, email: customer.email || undefined },
                theme: { color: "#F26522" },
                handler: async (resp) => {
                    try {
                        const result = await verifyPayment({
                            order_id: resp.razorpay_order_id,
                            payment_id: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                        });
                        setDone({ reference: result.reference, paymentId: resp.razorpay_payment_id });
                    } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail) || "Verification failed — if money was deducted, WhatsApp us the payment ID.");
                    }
                },
                modal: { ondismiss: () => toast("Payment window closed — your seats are not blocked yet.") },
            });
            if (order.discount) {
                toast.success(`Coupon ${order.coupon_code} applied — you saved ${inr(order.discount)}!`);
            } else if (order.associate_code) {
                toast.success(`Partner code ${order.associate_code} applied — this booking is credited to ${order.associate_name || "your partner"}.`);
            }
            rzp.on("payment.failed", (resp) => {
                toast.error(`Payment failed: ${resp.error?.description || "try another method"}. No seats blocked.`);
            });
            rzp.open();
            setOpen(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment right now.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="mt-3 rounded-xl border-2 border-saffron/50 bg-saffron/5 p-4" data-testid="pay-advance-block">
                <button onClick={() => setOpen(true)} data-testid="pay-advance-btn" className="w-full rounded-full bg-navy hover:bg-navy-light text-white font-display font-black text-sm uppercase tracking-wider py-3.5 transition-colors flex items-center justify-center gap-2">
                    <Lock className="h-4 w-4" /> Pay {inr(estimate.advance)} to Block Seats
                </button>
                <p className="mt-2 text-[11px] text-stone-500 text-center" data-testid="payment-plan-text">
                    {planText(pkg.advance_pct)} · Secure UPI/cards via Razorpay
                </p>
                <PaymentBadges />
                <TestPayButton />
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-navy-deep/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="pay-modal">
                    <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto overscroll-contain">
                        <div className="sticky -top-6 -mx-6 px-6 pt-5 pb-3 bg-white border-b border-stone-100 z-10 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="font-display font-black text-xl text-navy">Block your seats</h3>
                                <p className="text-xs text-stone-500 mt-0.5">{pkg.name} · {tier} · Advance {inr(estimate.advance)} of {inr(estimate.total)}</p>
                            </div>
                            <button onClick={() => setOpen(false)} data-testid="pay-close" className="text-stone-400 hover:text-navy"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="mt-5 space-y-4">
                            <div><label className={lab}>Full name *</label><input className={sel} value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} data-testid="pay-name" placeholder="Lead traveller's name" /></div>
                            <div><label className={lab}>Phone / WhatsApp *</label><input type="tel" inputMode="numeric" maxLength={12} className={sel} value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))} data-testid="pay-phone" placeholder="10-digit mobile" /></div>
                            <div><label className={lab}>Email (for receipt)</label><input type="email" className={sel} value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} data-testid="pay-email" placeholder="you@example.com" /></div>
                            <div>
                                <label className={lab}>Referral / coupon code (if you have one)</label>
                                <input className={sel} value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="pay-coupon" placeholder="GC-XXXXXXXX or partner code (GCE-XXXXXX)" />
                                <p className="mt-1 text-[10px] text-stone-400">Coupon codes give instant discount · partner codes credit your booking to them</p>
                                {getReferral() && !coupon.trim() && (
                                    <p className="mt-1 text-[10px] font-bold text-green-700" data-testid="auto-ref-note">Partner code {getReferral()} auto-applied from your referral link</p>
                                )}
                            </div>
                            <button onClick={startPayment} disabled={busy} data-testid="pay-now-btn" className="w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3.5 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Pay {inr(estimate.advance)} Securely
                            </button>
                            <p className="text-[11px] text-stone-400 text-center flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" /> Payment verified server-side. Blocking seats ≠ final booking — we confirm tickets & stays before the balance.</p>
                            <PaymentBadges />
                        </div>
                    </div>
                </div>
            )}

            {done && (
                <div className="fixed inset-0 z-50 bg-navy-deep/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pay-success">
                    <div className="bg-white w-full sm:max-w-md rounded-2xl p-8 text-center">
                        <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
                        <h3 className="mt-4 font-display font-black text-2xl text-navy">Advance received!</h3>
                        <p className="mt-2 text-sm text-stone-600">Booking reference <span className="font-black text-saffron" data-testid="pay-reference">{done.reference}</span></p>
                        <p className="mt-1 text-xs text-stone-400">Payment ID: {done.paymentId}</p>
                        <p className="mt-3 text-sm text-stone-600">Your seats are blocked. Our Trip Expert will call to confirm tickets & stays — balance follows your payment plan.</p>
                        <a
                            href={`https://wa.me/919930007445?text=${encodeURIComponent(`Hi GhumneChaley! I paid the advance for "${pkg.name}" (ref ${done.reference}). Payment ID: ${done.paymentId}`)}`}
                            target="_blank" rel="noreferrer" data-testid="pay-whatsapp-followup"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-xs font-display font-black uppercase tracking-wider hover:brightness-95"
                        >
                            <Share2 className="h-4 w-4" /> Send Receipt on WhatsApp
                        </a>
                        <button onClick={() => setDone(null)} data-testid="pay-success-close" className="mt-4 block mx-auto text-xs font-bold uppercase tracking-wider text-stone-400 hover:text-navy">Done</button>
                    </div>
                </div>
            )}
        </>
    );
}

/** Brand trust strip: payee line (BigKreation) + Visa/Mastercard/GooglePay/PhonePe/UPI/Netbanking + Secured by Razorpay. */
export function PaymentBadges() {
    const pill = "h-7 px-2.5 rounded-md bg-white border border-stone-200 flex items-center justify-center shrink-0";
    return (
        <div className="mt-3 rounded-xl bg-stone-100/80 border border-stone-200 px-3.5 py-3" data-testid="payment-badges">
            <p className="flex items-center justify-center gap-1.5 text-[12px] text-stone-600 text-center" data-testid="payment-payee-line">
                <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: "#2F8F5B" }} />
                <span>You're paying <span className="font-black text-navy">GhumneChaley.com</span> · a brand by <span className="font-bold text-navy">BigKreation Entertainment Pvt Ltd</span></span>
            </p>
            <div className="mt-2.5 flex flex-wrap items-center justify-center gap-1.5">
                <span className={pill} title="Visa" data-testid="badge-visa">
                    <span className="font-black italic text-[13px] tracking-tight" style={{ color: "#1A1F71" }}>VISA</span>
                </span>
                <span className={pill} title="Mastercard" data-testid="badge-mastercard">
                    <svg viewBox="0 0 36 22" className="h-4 w-auto" aria-label="Mastercard">
                        <circle cx="14" cy="11" r="8.5" fill="#EB001B" />
                        <circle cx="22" cy="11" r="8.5" fill="#F79E1B" fillOpacity="0.85" />
                    </svg>
                </span>
                <span className={pill} title="Google Pay" data-testid="badge-googlepay">
                    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 mr-1" aria-hidden="true">
                        <path d="M8 1.6a6.4 6.4 0 0 1 5.7 3.5l-2.3 1.5A3.6 3.6 0 1 0 8 11.6c1 0 1.9-.4 2.6-1H8V7.4h6.3c.1.4.1.9.1 1.3A6.4 6.4 0 1 1 8 1.6z" fill="#4285F4" />
                        <path d="M8 1.6a6.4 6.4 0 0 1 5.7 3.5l-.9.6A5.4 5.4 0 0 0 8 2.6z" fill="#EA4335" />
                        <path d="M2.2 5.3a6.4 6.4 0 0 0 0 5.4l1-.8a5.4 5.4 0 0 1 0-3.8z" fill="#FBBC05" />
                        <path d="M8 14.4c-2 0-3.8-.9-5-2.4l1-.8A5.4 5.4 0 0 0 8 13.4z" fill="#34A853" />
                    </svg>
                    <span className="font-bold text-[11px] text-stone-600">Pay</span>
                </span>
                <span className={pill} title="PhonePe" data-testid="badge-phonepe" style={{ backgroundColor: "#5F259F", borderColor: "#5F259F" }}>
                    <span className="font-black text-[11px] text-white tracking-tight">PhonePe</span>
                </span>
                <span className={pill} title="UPI" data-testid="badge-upi">
                    <svg viewBox="0 0 34 16" className="h-3.5 w-auto" aria-label="UPI">
                        <path d="M3 2 L8 8 L3 14" fill="none" stroke="#097939" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 2 L12 8 L7 14" fill="none" stroke="#ED752E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        <text x="14" y="12" fontSize="10" fontWeight="800" fill="#3D3D3D" fontFamily="Arial, sans-serif">UPI</text>
                    </svg>
                </span>
                <span className={pill} title="Netbanking" data-testid="badge-netbanking">
                    <svg viewBox="0 0 16 14" className="h-3.5 w-auto mr-1" aria-hidden="true">
                        <rect x="1" y="9" width="2.5" height="4" rx="0.5" fill="#4B5563" />
                        <rect x="5" y="6.5" width="2.5" height="6.5" rx="0.5" fill="#4B5563" />
                        <rect x="9" y="4" width="2.5" height="9" rx="0.5" fill="#4B5563" />
                        <rect x="13" y="1.5" width="2.5" height="11.5" rx="0.5" fill="#4B5563" />
                    </svg>
                    <span className="font-bold text-[11px] text-stone-600">Netbanking</span>
                </span>
                <span className="flex items-center gap-1 px-1 text-[11px] text-stone-500" data-testid="badge-razorpay">
                    <Lock className="h-3.5 w-3.5" style={{ color: "#2F8F5B" }} />
                    Secured by
                    <svg viewBox="0 0 12 16" className="h-3 w-auto" aria-hidden="true">
                        <path d="M7 0 L1 9 H5 L4 16 L11 6 H6.5 Z" fill="#3395FF" />
                    </svg>
                    <span className="font-black tracking-tight" style={{ color: "#3395FF" }}>Razorpay</span>
                </span>
            </div>
        </div>
    );
}

/** Subtle team-only ₹1 gateway smoke test — sits under the pay block. */
export function TestPayButton() {    const { Razorpay } = useRazorpay();
    const [busy, setBusy] = useState(false);
    const [ref, setRef] = useState(null);

    const run = async () => {
        setBusy(true);
        try {
            const order = await createTestPaymentOrder({ name: "Team Test" });
            const rzp = new Razorpay({
                key: order.key_id,
                amount: 100,
                currency: "INR",
                name: "GhumneChaley.com",
                description: "₹1 gateway test — safe to ignore",
                order_id: order.order_id,
                theme: { color: "#1B3A66" },
                handler: async (resp) => {
                    try {
                        const result = await verifyPayment({
                            order_id: resp.razorpay_order_id,
                            payment_id: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                        });
                        setRef(result.reference);
                        toast.success("₹1 test payment verified — check the Razorpay dashboard and admin leads.");
                    } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail) || "Verification failed.");
                    }
                },
            });
            rzp.on("payment.failed", () => toast.error("Test payment failed — the failure path works too."));
            rzp.open();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not start test payment.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-2 text-center">
            <button onClick={run} disabled={busy} data-testid="test-payment-btn" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-400 hover:text-saffron transition-colors disabled:opacity-60">
                {busy && <Loader2 className="h-3 w-3 animate-spin" />} Team testing? Send a ₹1 test payment
            </button>
            {ref && <p className="mt-1 text-[11px] font-bold text-green-700" data-testid="test-payment-ref">₹1 received · ref {ref}</p>}
        </div>
    );
}
